// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useReservation } from '../../contexts/ReservationContext.tsx';
import { useRestaurant } from '../../contexts/RestaurantContext.tsx';
import ReservationSteps from '../../components/public/ReservationSteps.tsx';
import DateTimePicker from '../../components/public/DateTimePicker.tsx';
import GuestInfoForm from '../../components/public/GuestInfoForm.tsx';
import TableSelector from '../../components/public/TableSelector.tsx';
import ConfirmationView from '../../components/public/ConfirmationView.tsx';
import { fetchRestaurantSettingsPublic } from '../../services/api.ts';
import { formatDateToLocalISO } from '../../utils/date.ts';
import { usesTableMap } from '../../utils/roomMode.ts';
import '../../styles/public-reservation.css';
import { toast } from 'react-toastify';

type ReservationData = {
  date: Date | null;
  time: string;
  guests: number;
  name: string;
  phone: string;
  email: string;
  birthdate: string;
  tableId: string;
  tableIds: string[];
  notes: string;
  salaId: string;
};

const getFirstSearchValue = (searchParams: URLSearchParams, keys: string[]) => {
  for (const key of keys) {
    const value = searchParams.get(key);
    if (value && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

const formatPrefilledPhone = (value: string) => {
  const trimmed = value.trim();
  const digits = trimmed.replace(/\D/g, '');
  const localDigits = digits.length > 11 && digits.startsWith('55')
    ? digits.slice(2)
    : digits;

  if (localDigits.length === 10) {
    return `(${localDigits.slice(0, 2)}) ${localDigits.slice(2, 6)}-${localDigits.slice(6)}`;
  }

  if (localDigits.length === 11) {
    return `(${localDigits.slice(0, 2)}) ${localDigits.slice(2, 7)}-${localDigits.slice(7)}`;
  }

  return trimmed;
};

const normalizeDateValue = (value: string) => {
  const normalized = value.trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : '';
};

const buildInitialReservationData = (searchParams: URLSearchParams): ReservationData => {
  const guestsParam = getFirstSearchValue(searchParams, ['pessoas', 'guests', 'numero_pessoas']);
  const parsedGuests = Number.parseInt(guestsParam, 10);

  return {
    date: null,
    time: '',
    guests: Number.isFinite(parsedGuests) && parsedGuests > 0 ? parsedGuests : 2,
    name: getFirstSearchValue(searchParams, ['nome', 'name', 'nome_cliente']),
    phone: formatPrefilledPhone(getFirstSearchValue(searchParams, ['telefone', 'phone', 'telefone_cliente'])),
    email: getFirstSearchValue(searchParams, ['email', 'email_cliente']),
    birthdate: normalizeDateValue(getFirstSearchValue(searchParams, ['nascimento', 'birthdate', 'nascimento_cliente'])),
    tableId: '',
    tableIds: [],
    notes: '',
    salaId: '',
  };
};

const PublicReservation: React.FC = () => {
  const { createReservation } = useReservation();
  const { floors, currentFloor, setCurrentFloor } = useRestaurant();
  const [searchParams] = useSearchParams();

  const [restaurantSlug, setRestaurantSlug] = useState('gourmex');
  const [usarMapaMesas, setUsarMapaMesas] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [managementLink, setManagementLink] = useState('');
  const [reservationData, setReservationData] = useState<ReservationData>(() => (
    buildInitialReservationData(searchParams)
  ));

  useEffect(() => {
    const pathParts = window.location.pathname.split('/');
    const slugIndex = pathParts.findIndex(part => part === 'reservar');
    if (slugIndex !== -1 && pathParts[slugIndex + 1]) {
      setRestaurantSlug(pathParts[slugIndex + 1]);
    }
  }, []);

  useEffect(() => {
    let active = true;
    const loadSettings = async () => {
      try {
        const settings = await fetchRestaurantSettingsPublic(restaurantSlug);
        if (active) {
          setUsarMapaMesas(settings?.reservo_usar_mapa_mesas !== false);
        }
      } catch (error) {
        console.error('Erro ao carregar configuração pública do mapa de mesas:', error);
      } finally {
        if (active) setSettingsLoaded(true);
      }
    };
    void loadSettings();
    return () => {
      active = false;
    };
  }, [restaurantSlug]);

  const showTableStep = useMemo(
    () => floors.some(floor => usesTableMap(floor, usarMapaMesas) && floor.modo_layout),
    [floors, usarMapaMesas]
  );

  const steps = useMemo(() => {
    const base = [
      { key: 'date', title: 'Data e Pessoas' },
      ...(showTableStep ? [{ key: 'table', title: 'Selecione a Mesa' }] : []),
      { key: 'guest', title: 'Informações do Cliente' },
      { key: 'confirm', title: 'Confirmação' }
    ];

    return base.map((step, index) => ({
      number: index + 1,
      title: step.title
    }));
  }, [showTableStep]);

  const totalSteps = steps.length;

  useEffect(() => {
    if (currentStep > totalSteps) {
      setCurrentStep(totalSteps);
    }
  }, [currentStep, totalSteps]);


  const updateReservationData = (data: Partial<typeof reservationData>) => {
    setReservationData(prev => ({
      ...prev,
      ...data
    }));
  };

  const handleEnvironmentChange = (salaId: string) => {
    const normalizedId = salaId?.trim();

    setReservationData(prev => {
      const effectiveId = normalizedId || prev.salaId;
      const floor = floors.find(item => String(item.id) === String(effectiveId));
      let guests = prev.guests;

      if (floor) {
        const min = floor.min_pessoas_por_reserva ?? 1;
        const maxCandidate = floor.max_pessoas_por_reserva ?? floor.capacidade_maxima ?? 20;
        const max = Math.max(min, maxCandidate);

        if (guests < min) {
          guests = min;
        } else if (guests > max) {
          guests = max;
        }
      }

      return {
        ...prev,
        salaId: effectiveId ? String(effectiveId) : prev.salaId,
        guests,
        tableId: '',
        tableIds: []
      };
    });

    if (normalizedId && normalizedId !== 'undefined' && normalizedId !== 'null') {
      setCurrentFloor(normalizedId);
    }
  };

  const handleNextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleRestart = () => {window.location.reload(); };

  const showEnvironmentSelector = !showTableStep && floors.length > 1;

  const rawSelectedSalaId = showTableStep
    ? currentFloor
    : (showEnvironmentSelector ? reservationData.salaId : (reservationData.salaId || floors[0]?.id || ''));

  const selectedSalaId = rawSelectedSalaId !== undefined && rawSelectedSalaId !== null
    ? String(rawSelectedSalaId)
    : '';

  const selectedSala = floors.find(floor => String(floor.id) === selectedSalaId);

  const selectedSalaHasLayout = usesTableMap(selectedSala, usarMapaMesas) && selectedSala?.modo_layout !== false;
  const guestLimits = !selectedSalaHasLayout && selectedSala
    ? {
        min: selectedSala.min_pessoas_por_reserva ?? 1,
        max: Math.max(
          selectedSala.min_pessoas_por_reserva ?? 1,
          selectedSala.max_pessoas_por_reserva ?? selectedSala.capacidade_maxima ?? 20
        )
      }
    : undefined;

  useEffect(() => {
    if (!floors.length || showTableStep || showEnvironmentSelector) {
      return;
    }

    const fallbackFloorId = floors[0]?.id;
    if (!fallbackFloorId) {
      return;
    }

    setReservationData(prev => {
      const salaIdCandidate = prev.salaId || fallbackFloorId;
      const salaId = salaIdCandidate !== undefined && salaIdCandidate !== null
        ? String(salaIdCandidate)
        : '';
      const floor = floors.find(item => String(item.id) === salaId) || floors[0];
      const min = floor?.min_pessoas_por_reserva ?? 1;
      const maxCandidate = floor?.max_pessoas_por_reserva ?? floor?.capacidade_maxima ?? 20;
      const max = Math.max(min, maxCandidate);
      let guests = prev.guests;

      if (guests < min) {
        guests = min;
      } else if (guests > max) {
        guests = max;
      }

      if (prev.salaId === salaId && prev.guests === guests) {
        return prev;
      }

      return { ...prev, salaId, guests };
    });

    if (!currentFloor) {
      setCurrentFloor(fallbackFloorId);
    }
  }, [floors, showTableStep, showEnvironmentSelector, currentFloor, setCurrentFloor]);

  const handleComplete = async (): Promise<boolean> => {
    if (!reservationData.date) {
      toast.warning('Selecione a data da reserva antes de confirmar.');
      return false;
    }

    if (!reservationData.time) {
      toast.warning('Selecione um horário para a reserva antes de confirmar.');
      return false;
    }

    const baseSalaId = showTableStep
      ? currentFloor
      : (reservationData.salaId || floors[0]?.id || currentFloor);

    const selectedTableIds = showTableStep && usesTableMap(
      floors.find((floor) => String(floor.id) === String(baseSalaId)), usarMapaMesas
    )
      ? reservationData.tableIds.filter((tableId) => String(tableId).trim())
      : [];

    const tableFloor = selectedTableIds.length > 0
      ? floors.find(floor =>
          floor.tables?.some(table => selectedTableIds.some((tableId) => String(table.id) === String(tableId)))
        )
      : undefined;

    const rawSalaId = tableFloor?.id ?? baseSalaId;
    const normalizedSalaId = rawSalaId !== null && rawSalaId !== undefined
      ? String(rawSalaId).trim()
      : '';

    if (!normalizedSalaId || normalizedSalaId === 'undefined' || normalizedSalaId === 'null') {
      toast.error('Não foi possível identificar o ambiente da reserva.');
      return false;
    }

    const activeSala = floors.find(floor => String(floor.id) === normalizedSalaId);

    if (showTableStep && usesTableMap(activeSala, usarMapaMesas)
      && activeSala?.modo_layout !== false && selectedTableIds.length === 0) {
      toast.warning('Selecione ao menos uma mesa disponível antes de continuar.');
      return false;
    }

    const mesaIds = selectedTableIds.map((tableId) => String(tableId).trim()).filter(Boolean);
    const mesaId = mesaIds[0] || null;

    const reservationWithSalaId = {
      data: formatDateToLocalISO(reservationData.date),
      hora: reservationData.time,
      nome_cliente: reservationData.name,
      mesa_id: mesaId,
      mesa_ids: mesaIds,
      numero_pessoas: reservationData.guests,
      sala_id: normalizedSalaId,
      telefone_cliente: reservationData.phone,
      email_cliente: reservationData.email || null,
      nascimento_cliente: reservationData.birthdate || null,
      obs: reservationData.notes
    };

    try {
      const createdReserva = await createReservation(reservationWithSalaId);
      setManagementLink(createdReserva?.link_gerenciamento || '');
      toast.success(
        createdReserva?.link_gerenciamento
          ? 'Reserva enviada com sucesso! Enviamos o link de alteração e cancelamento pelo WhatsApp.'
          : 'Reserva enviada com sucesso!'
      );
      return true;
    } catch (error: any) {
      console.error('Erro ao criar reserva publica:', error);
      const backendMessage = error?.response?.data?.error;
      toast.error(backendMessage || 'Não foi possível concluir a reserva. Tente novamente.');
      return false;
    }
  };

  const dateStepNumber = 1;
  const tableStepNumber = showTableStep ? 2 : null;
  const guestInfoStepNumber = showTableStep ? 3 : 2;
  const confirmationStepNumber = showTableStep ? 4 : 3;

  if (!settingsLoaded) {
    return <div className="public-reservation loading">Carregando configurações...</div>;
  }

  return (
    <div className="public-reservation">
      <div className="reservation-container">
        <h1>Reserve sua Mesa</h1>

        <ReservationSteps
          steps={steps}
          currentStep={currentStep}
        />

        <div className="step-content">
          {currentStep === dateStepNumber && (
            <DateTimePicker
              value={reservationData}
              restaurantSlug={restaurantSlug}
              onChange={updateReservationData}
              onNext={handleNextStep}
              showEnvironmentSelector={showEnvironmentSelector}
              environments={floors}
              selectedEnvironmentId={selectedSalaId}
              onEnvironmentChange={handleEnvironmentChange}
              guestLimits={guestLimits}
            />
          )}

          {tableStepNumber && currentStep === tableStepNumber && (
            <TableSelector
              selectedDate={reservationData.date}
              selectedTime={reservationData.time}
              partySize={reservationData.guests}
              selectedTableIds={reservationData.tableIds}
              restaurantSlug={restaurantSlug}
              legacyMapDefault={usarMapaMesas}
              onTableSelect={(tableIds) => updateReservationData({
                tableIds,
                tableId: tableIds[0] || ''
              })}
              onNext={handleNextStep}
              onBack={handleRestart}
            />
          )}

          {currentStep === guestInfoStepNumber && (
            <GuestInfoForm
              value={reservationData}
              onChange={updateReservationData}
              onNext={handleNextStep}
              onBack={handleRestart}
            />
          )}

          {currentStep === confirmationStepNumber && (
            <ConfirmationView
              reservationData={reservationData}
              restaurantSlug={restaurantSlug}
              managementLink={managementLink}
              onConfirm={handleComplete}
              onBack={handleRestart}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicReservation;
